import { ObjectId } from '@racemap/sdk/schema/base';
import {
  type EntityEvent,
  EntityEventSchema,
  EntityEventState,
  type EntityEventStateWithoutNew,
} from '@racemap/sdk/schema/entityEvents';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import type { PointWithSpeedAndOffset } from '@racemap/utilities/functions/timing';
import { sleep } from '@racemap/utilities/functions/utils';
import * as Comlink from 'comlink';
import { produce } from 'immer';
import type mapboxgl from 'mapbox-gl';
import { basicAlertEventTypes } from 'src/components/MonitorBeta/Alerts';
import type { SelectedStarterAlertMenu } from 'src/components/MonitorBeta/StarterAlertsMenu';
import type { SelectedStarterGraphInfoMenu } from 'src/components/MonitorBeta/StarterGraphInfoMenu';
import type { StarterTracksManagement } from 'src/worker/starter-tracks-management';
import { z } from 'zod';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { showAlertNotification } from './showAlertNotification';

const apiClient = RacemapAPIClient.fromWindowLocation();

interface Target {
  center: mapboxgl.FlyToOptions['center'];
  zoom: mapboxgl.FlyToOptions['zoom'];
}

interface MonitorActions {
  setEventId: (eventIdOrSlug: string | ObjectId) => Promise<void>;
  initStarterTracksManagement: () => Promise<void>;
  updateStarterTracks: () => Promise<void>;
  getStarterTrack: (starterId: string) => Promise<Array<PointWithSpeedAndOffset>>;
  setSelectedStarterId: (starterId: string | null) => void;
  setMapInstance: (mapInstance: mapboxgl.Map | null) => void;
  flyTo: (target: Target) => Promise<void>;
  getFrontendGeneratedEntityEvents: () => Promise<Map<string, EntityEvent>>;
  loadAlerts: () => Promise<void>;
  initMonitoring: (eventSlug: string) => Promise<void>;
  updateAlertState: (
    alertIds: Array<string | ObjectId>,
    newState: EntityEventStateWithoutNew,
  ) => Promise<void>;
  assignAlertToMyself: (alertId: string | ObjectId) => Promise<void>;
  resolveAlert: (alertId: string | ObjectId) => Promise<void>;
}

interface MonitorState {
  eventId: ObjectId | null;
  alerts: Map<string, EntityEvent>;
  starterTracksManagement: Comlink.Remote<StarterTracksManagement> | null;
  isLoadingTracks: boolean;
  isReady: boolean;
  selectedStarterAlertMenu: SelectedStarterAlertMenu;
  selectedStarterGraphInfoMenu: SelectedStarterGraphInfoMenu;
  selectedStarterId: string | null;
  mapInstance: mapboxgl.Map | null;
  actions: MonitorActions;
}

export const useMonitorStore = create<MonitorState>((set, get) => ({
  eventId: null,
  alerts: new Map(),
  starterTracksManagement: null,
  isLoadingTracks: false,
  isReady: false,
  selectedStarterAlertMenu: 'starters',
  selectedStarterGraphInfoMenu: 'graph',
  selectedStarterId: null,
  mapInstance: null,
  actions: {
    setEventId: async (eventIdOrSlug) => {
      if (ObjectId.isValid(eventIdOrSlug)) {
        set({ eventId: new ObjectId(eventIdOrSlug) });
      }

      const event = await apiClient.getEvent(eventIdOrSlug.toString());
      if (event == null) {
        throw new Error('Event not found');
      }

      set({ eventId: new ObjectId(event.id) });
    },
    setMapInstance: (mapInstance: mapboxgl.Map | null) => set({ mapInstance }),
    flyTo: async (target: Target) => {
      const { mapInstance } = get();
      if (mapInstance == null) {
        throw new Error('Map instance is not set');
      }

      mapInstance.flyTo({
        center: target.center,
        zoom: target.zoom,
        bearing: 0,
        pitch: 0,
        duration: 1000,
        essential: false,
      });
      return Promise.race([
        new Promise<void>((resolve) => {
          mapInstance.once('moveend', () => {
            resolve();
          });
        }),
        sleep(1000),
      ]);
    },
    initStarterTracksManagement: async () => {
      try {
        const { starterTracksManagement, eventId } = get();
        if (eventId == null || !ObjectId.isValid(eventId)) {
          throw new Error('Invalid event ID');
        }

        if (starterTracksManagement != null) {
          return;
        }

        if (window.Worker == null) {
          throw new Error('Web Workers are not supported');
        }

        const remoteClass = Comlink.wrap<StarterTracksManagement>(
          new Worker(new URL('../../worker/starter-tracks-management.ts', import.meta.url), {
            type: 'module',
          }),
        );

        // @ts-expect-error Comlink typings are incorrect
        const management = (await new remoteClass({
          eventId: eventId.toHexString(),
          host: apiClient._host,
        })) as Comlink.Remote<StarterTracksManagement>;

        // @ts-expect-error Comlink typings are incorrect
        management.onReady = Comlink.proxy(async () => set({ isReady: true }));
        // @ts-expect-error Comlink typings are incorrect
        management.onUpdateStart = Comlink.proxy(async () => set({ isLoadingTracks: true }));
        // @ts-expect-error Comlink typings are incorrect
        management.onUpdateFinish = Comlink.proxy(() => set({ isLoadingTracks: false }));

        await management.init();
        set({ starterTracksManagement: management });
      } catch (error) {
        console.error('Failed to initialize StarterTracksManagement', error);
      }
    },
    updateStarterTracks: async () => {
      try {
        const { starterTracksManagement } = get();
        if (starterTracksManagement == null) {
          throw new Error('StarterTracksManagement is not initialized');
        }

        await starterTracksManagement.update();
      } catch (error) {
        console.error('Failed to update starter tracks', error);
      }
    },
    getStarterTrack: async (starterId) => {
      const { starterTracksManagement } = get();
      if (starterTracksManagement == null) {
        throw new Error('StarterTracksManagement is not initialized');
      }

      return starterTracksManagement.getStarterTrack(starterId);
    },
    getFrontendGeneratedEntityEvents: async () => {
      const { starterTracksManagement } = get();
      if (starterTracksManagement == null) {
        throw new Error('StarterTracksManagement is not initialized');
      }
      const events = starterTracksManagement.getFrontendGeneratedEntityEvents();

      return z.map(z.string(), EntityEventSchema).parse(events);
    },
    setSelectedStarterId: (starterId: string | null) => {
      if (get().selectedStarterId === starterId) {
        set({ selectedStarterId: null });
        return;
      }

      set({ selectedStarterId: starterId });
    },

    loadAlerts: async () => {
      const eventId = get().eventId;

      if (eventId === null) {
        throw new Error('Event ID is not set');
      }

      const previousAlerts = get().alerts;

      const fetchedBackend = await apiClient.getEntityEventsByEvent(eventId, {
        types: basicAlertEventTypes,
      });
      const backendMap = new Map<string, EntityEvent>();
      for (const alert of fetchedBackend) {
        backendMap.set(alert.id.toHexString(), alert);
      }

      let frontendMap = new Map<string, EntityEvent>();
      try {
        const raw = await get().actions.getFrontendGeneratedEntityEvents();
        frontendMap = raw;
      } catch (err) {
        console.warn('Could not load frontend events', err);
      }

      const newAlerts = new Map([...backendMap, ...frontendMap]);
      if (previousAlerts.size > 0) {
        for (const [id, alert] of newAlerts) {
          if (!previousAlerts.has(id)) {
            const deviceId = alert.entityId.toHexString();
            let starterName = deviceId;
            let starterNumber = 'Unknown';

            // TODO: Fetch starters in the Notification component

            try {
              const starters = await apiClient.getEventStarters(eventId.toString(), {
                deviceId,
              });
              if (starters.length !== 1) {
                throw new Error('Invalid number of starters');
              }
              const { name, startNumber } = starters[0];
              starterName = name ?? 'Undefined name';
              starterNumber = startNumber ?? 'Undefined number';
            } catch (err) {
              console.warn('Failed to fetch starter info, falling back to defaults:', err);
            }

            showAlertNotification({
              alert,
              starterNumber,
              starterName,
              onAssign: async (alertId) => {
                await get().actions.assignAlertToMyself(alertId);
              },
              onResolve: async (alertId) => {
                await get().actions.resolveAlert(alertId);
              },
            });
          }
        }
      }
      set({ alerts: newAlerts });
    },

    initMonitoring: async (eventSlug: string) => {
      try {
        await get().actions.setEventId(eventSlug);
        await get().actions.loadAlerts();
      } catch (error) {
        console.error('Error initializing alerts:', error);
      }
    },

    updateAlertState: async (
      alertIds: Array<string | ObjectId>,
      newState: EntityEventStateWithoutNew,
    ) => {
      try {
        const updatedAlerts = await apiClient.updateEntityEventState(alertIds, newState);
        set(
          produce((s: MonitorState) => {
            updatedAlerts.forEach((alert) => s.alerts.set(alert.id.toString(), alert));
          }),
        );
      } catch (error) {
        console.error('Failed to update alert state', error);
        throw error;
      }
    },

    assignAlertToMyself: async (alertId: string | ObjectId) => {
      await get().actions.updateAlertState([alertId], EntityEventState.IN_PROGRESS);
    },

    resolveAlert: async (alertId: string | ObjectId) => {
      await get().actions.updateAlertState([alertId], EntityEventState.RESOLVED);
    },
  },
}));

export const useSelectedStarterAlertMenu = () =>
  useMonitorStore((state) => state.selectedStarterAlertMenu);
export const useSelectedStarterGraphInfoMenu = () =>
  useMonitorStore((state) => state.selectedStarterGraphInfoMenu);
export const useSelectedStarterId = () => useMonitorStore((state) => state.selectedStarterId);
export const useMonitorActions = () => useMonitorStore((state) => state.actions);
export const useEventId = () => useMonitorStore((state) => state.eventId);
export const useAlerts = () =>
  useMonitorStore(useShallow((state) => Array.from(state.alerts.values())));
